import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ReactPlayer from 'react-player';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import PageLayout from '../../components/PageLayout';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import EmailIcon from '@mui/icons-material/Email';
import HandshakeIcon from '@mui/icons-material/Handshake';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '3rem',
  color: '#1a1a1a',
  marginBottom: '2rem',
  textAlign: 'center',
});

const StyledPaper = styled(Paper)({
  padding: '2rem',
  marginBottom: '2rem',
  backgroundColor: '#fff',
  boxShadow: 'none',
  border: '1px solid #eee',
  borderRadius: '8px',
});

const StyledText = styled(Typography)({
  fontSize: '1.1rem',
  lineHeight: '1.8',
  color: '#333',
  marginBottom: '1.5rem',
});

const IconWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  '& .MuiSvgIcon-root': {
    fontSize: '3rem',
    color: '#c4a661',
  },
});

const StepTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2rem',
  color: '#1a1a1a',
  marginBottom: '1rem',
  textAlign: 'center',
});

const CustomButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem 2rem',
  marginTop: '1rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
});

const CustomInquiriesPage = () => {
  const [isMuted, setIsMuted] = useState(true);

  return (
    <PageLayout>
      <Container>
        <Box sx={{ py: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageTitle>Custom Jewelry Inquiries</PageTitle>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              align="center"
              sx={{ mb: 6, fontSize: '1.2rem' }}
            >
              Transform your vision into a unique piece of jewelry
            </Typography>

            <Box sx={{ display: 'flex', gap: 4, mb: 6, position: 'relative' }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <StyledPaper>
                      <IconWrapper>
                        <DesignServicesIcon />
                      </IconWrapper>
                      <StepTitle>1. Share Your Vision</StepTitle>
                      <StyledText>
                        Send us your logo, brand concept, or design inspiration. The more details you provide,
                        the better we can understand and execute your vision.
                      </StyledText>
                    </StyledPaper>
                  </motion.div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <StyledPaper>
                      <IconWrapper>
                        <EmailIcon />
                      </IconWrapper>
                      <StepTitle>2. Contact Us</StepTitle>
                      <StyledText>
                        Email us at customconnectjewelry@gmail.com with your concept details and any reference images
                        or inspiration pieces you'd like to share.
                      </StyledText>
                    </StyledPaper>
                  </motion.div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    <StyledPaper>
                      <IconWrapper>
                        <HandshakeIcon />
                      </IconWrapper>
                      <StepTitle>3. Consultation</StepTitle>
                      <StyledText>
                        We'll review your inquiry and reach out within 2 business days to schedule a consultation
                        to discuss your custom piece in detail, including timeline and pricing.
                      </StyledText>
                    </StyledPaper>
                  </motion.div>
                </Grid>
              </Grid>

              {/* Smiles Section */}
              <Box sx={{ 
                width: '280px', 
                flexShrink: 0, 
                position: 'absolute',
                right: '-320px',
                top: 0
              }}>
                <Typography
                  variant="h5"
                  align="center"
                  sx={{
                    fontFamily: 'Cormorant Garamond, serif',
                    mb: 3,
                  }}
                >
                  Custom Smiles
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '250px',
                          overflow: 'hidden',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          backgroundColor: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <img
                          src="/smiles/83334FAA-A635-412A-A061-3C89DDB7729E.jpg"
                          alt="Custom Smile"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    </motion.div>
                  </Grid>
                  <Grid item xs={12}>
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '250px',
                          overflow: 'hidden',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          backgroundColor: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src="/smiles/IMG_1999.jpg"
                          alt="Custom Smile"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    </motion.div>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h4"
                align="center"
                sx={{
                  fontFamily: 'Cormorant Garamond, serif',
                  mb: 4,
                }}
              >
                Our Custom Work
              </Typography>
              
              {/* Videos Section */}
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '400px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    >
                      <ReactPlayer
                        url="/gallery/IMG_0072.mov"
                        controls={true}
                        width="100%"
                        height="100%"
                        playsinline={true}
                        playing={true}
                        loop={true}
                        muted={isMuted}
                        config={{
                          file: {
                            forceVideo: true,
                            attributes: {
                              playsInline: true,
                              controlsList: 'nodownload'
                            }
                          }
                        }}
                        style={{
                          objectFit: 'contain',
                          backgroundColor: '#000'
                        }}
                      />
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '400px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    >
                      <ReactPlayer
                        url="/gallery/Video_1.mov"
                        controls={true}
                        width="100%"
                        height="100%"
                        playsinline={true}
                        playing={true}
                        loop={true}
                        muted={isMuted}
                        config={{
                          file: {
                            forceVideo: true,
                            attributes: {
                              playsInline: true,
                              controlsList: 'nodownload'
                            }
                          }
                        }}
                        style={{
                          objectFit: 'contain',
                          backgroundColor: '#000'
                        }}
                      />
                    </Box>
                  </motion.div>
                </Grid>
              </Grid>

              <Box sx={{ textAlign: 'center', mt: 2, mb: 4 }}>
                <Button
                  variant="contained"
                  onClick={() => setIsMuted(!isMuted)}
                  sx={{
                    backgroundColor: isMuted ? '#1a1a1a' : '#c4a661',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: isMuted ? '#333' : '#d4b671',
                    },
                  }}
                >
                  {isMuted ? 'Unmute Videos' : 'Mute Videos'}
                </Button>
              </Box>

              {/* Images Row */}
              <Grid container spacing={4} sx={{ mt: 4 }}>
                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '400px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src="/gallery/IMG_2006.jpg"
                        alt="Custom Jewelry Piece"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '400px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src="/gallery/IMG_2010.jpg"
                        alt="Custom Jewelry Piece"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  </motion.div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '400px',
                        overflow: 'hidden',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src="/gallery/IMG_2110.jpg"
                        alt="Custom Jewelry Piece"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  </motion.div>
                </Grid>
              </Grid>
            </Box>

            <StyledPaper>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    fontFamily: 'Cormorant Garamond, serif',
                    mb: 3,
                  }}
                >
                  Ready to Create Your Custom Piece?
                </Typography>
                <StyledText>
                  Build your piece in the Design Studio — choose the metal, the stone
                  and the setting, turn it in your hands, and send the finished
                  specification straight to our bench.
                </StyledText>

                <CustomButton
                  component={RouterLink}
                  to="/design"
                  variant="contained"
                  sx={{ mt: 3 }}
                >
                  Open the Design Studio
                </CustomButton>

                <Typography sx={{ mt: 3, fontSize: '0.9rem', color: '#777' }}>
                  Prefer to describe it in your own words, or have reference images
                  to send?{' '}
                  <Box
                    component="a"
                    href="mailto:customconnectjewelry@gmail.com?subject=Custom Jewelry Inquiry&body=Hello,%0D%0A%0D%0AI'm interested in creating a custom piece.%0D%0A%0D%0AHere are the details of what I'm looking for:%0D%0A-%20Type of piece:%0D%0A-%20Design inspiration:%0D%0A-%20Materials preferred:%0D%0A-%20Budget range:%0D%0A%0D%0AThank you!"
                    sx={{ color: '#c4a661', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Email us instead
                  </Box>
                  .
                </Typography>
              </Box>
            </StyledPaper>
          </motion.div>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default CustomInquiriesPage;
